import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Switch, Route, NavLink, Redirect, useLocation } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Drawer, Button, Popconfirm } from 'antd';
import { UnorderedListOutlined, BgColorsOutlined, BarChartOutlined, SettingOutlined, EyeOutlined, ExportOutlined, LogoutOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api, { logout } from './api';
import ProfileView from '../components/ProfileView';
import LinksPanel from './LinksPanel';
import DesignPanel from './DesignPanel';
import AnalyticsPanel from './AnalyticsPanel';
import SettingsPanel from './SettingsPanel';
import { DARK_THEME } from './theme';
import './dashboard.css';

const NAV = [
  { to: '/admin', exact: true, key: 'links', icon: UnorderedListOutlined },
  { to: '/admin/design', key: 'design', icon: BgColorsOutlined },
  { to: '/admin/analytics', key: 'analytics', icon: BarChartOutlined },
  { to: '/admin/settings', key: 'settings', icon: SettingOutlined }
];

const Wordmark = () => (
  <a className="dash-wordmark" href="/">
    <span className="dash-wordmark__keys" aria-hidden="true"><i /><i className="is-black" /><i /><i className="is-black" /><i /></span>
    Piyanos Link
  </a>
);

const PhonePreview = ({ user }) => (
  <div className="dash-phone">
    <div className="dash-phone__screen">
      <ProfileView user={user} className="dash-phone__profile" preview />
    </div>
  </div>
);

// Grey blocks in the shape of the links panel, shown while the account
// loads so the layout does not jump when the real content arrives
const PanelSkeleton = () => (
  <div className="dash-panel dash-skeleton" aria-hidden="true">
    <i className="dash-skeleton__title" />
    <i className="dash-skeleton__bar" />
    <i className="dash-skeleton__cta" />
    <i className="dash-skeleton__blocks" />
    {[0, 1, 2, 3, 4].map(i => <i key={i} className="dash-skeleton__row" />)}
  </div>
);

const Dashboard = () => {
  const [t] = useTranslation();
  const location = useLocation();
  const [user, setUser] = useState(null);
  // The last copy of the user the server confirmed. Panels apply some edits
  // before the server answers (colour drags, templates, reordering); when a
  // write fails, this is what the panel and the preview go back to.
  const confirmed = useRef(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(() => {
    setLoadFailed(false);
    api.me()
      .then(loaded => { confirmed.current = loaded; setUser(loaded); })
      .catch(err => {
        // Tokens expire; a stale one goes back to the login page
        const status = err.response && err.response.status;
        if (status === 400 || status === 401) {
          localStorage.removeItem('auth-token');
          window.location = '/login';
          return;
        }
        setLoadFailed(true);
      });
  }, []);

  useEffect(load, [load]);

  // "Linkler · Piyanos Link", following the tab and the language
  const navKey = (NAV.find(item => (item.exact ? location.pathname.replace(/\/$/, '') === item.to : location.pathname.indexOf(item.to) === 0)) || NAV[0]).key;
  useEffect(() => {
    document.title = `${t(`dash.nav.${navKey}`)} · Piyanos Link`;
  }, [t, navKey]);

  // Panels hand every write here: it applies the server's copy of the user,
  // which keeps the preview and all panels in step. A failed write puts the
  // last confirmed copy back, so nothing on screen claims a change the live
  // page does not have; the caller still gets the error to report it.
  const save = useCallback(promise => promise.then(
    updated => { confirmed.current = updated; setUser(updated); return updated; },
    err => { if (confirmed.current) setUser(confirmed.current); throw err; }
  ), []);

  const publicUrl = user ? `/${user.username}` : null;
  const panelProps = { user, setUser, save };

  let main;
  if (user) {
    main = (
      <Switch>
        <Route exact path="/admin" render={() => <LinksPanel {...panelProps} />} />
        <Route path="/admin/design" render={() => <DesignPanel {...panelProps} />} />
        <Route path="/admin/analytics" render={() => <AnalyticsPanel {...panelProps} />} />
        <Route path="/admin/settings" render={() => <SettingsPanel {...panelProps} />} />
        <Redirect to="/admin" />
      </Switch>
    );
  } else if (loadFailed) {
    main = (
      <div className="dash-panel dash-panel--center">
        <div className="dash-state" role="alert">
          <p className="dash-state__title">{t('dash.loadFailedTitle')}</p>
          <p className="dash-muted">{t('dash.loadFailed')}</p>
          <Button type="primary" icon={<ReloadOutlined />} onClick={load}>{t('dash.retry')}</Button>
        </div>
      </div>
    );
  } else {
    main = <PanelSkeleton />;
  }

  return (
    <ConfigProvider theme={DARK_THEME}>
      {/* AntApp hands message/notification a context, so toasts come out in
          the dark theme instead of antd's default white */}
      <AntApp component={false}>
        <div className="dash">
          <aside className="dash-nav">
            <Wordmark />
            <nav className="dash-nav__links" aria-label={t('dash.menu')}>
              {NAV.map(({ to, exact, key, icon: Icon }) =>
                <NavLink key={key} to={to} exact={exact} className="dash-nav__link" activeClassName="is-active">
                  <Icon /><span>{t(`dash.nav.${key}`)}</span>
                </NavLink>
              )}
              {/* Below 1180px there is no side preview; it opens from the
                  menu (a tab on phones) rather than a floating button that
                  would sit on top of the rows' switches */}
              {user &&
                <button type="button" className="dash-nav__link dash-nav__preview" onClick={() => setPreviewOpen(true)}>
                  <EyeOutlined /><span>{t('dash.preview')}</span>
                </button>}
            </nav>
            <div className="dash-nav__foot">
              {publicUrl &&
                <a className="dash-nav__link" href={publicUrl} target="_blank" rel="noopener noreferrer">
                  <ExportOutlined /><span>{t('dash.viewPage')}</span>
                </a>}
              <Popconfirm title={t('dash.logoutConfirm')} okText={t('dash.logout')} cancelText={t('dash.cancel')} onConfirm={logout} placement="topLeft">
                <button type="button" className="dash-nav__link"><LogoutOutlined /><span>{t('dash.logout')}</span></button>
              </Popconfirm>
            </div>
          </aside>

          <main className="dash-main" aria-busy={!user && !loadFailed}>
            {main}
          </main>

          <aside className="dash-preview" aria-label={t('dash.preview')}>
            <div className="dash-preview__label">{t('dash.preview')}</div>
            {user ? <PhonePreview user={user} /> : <div className={`dash-phone dash-phone--empty ${loadFailed ? 'is-static' : ''}`} />}
          </aside>

          {user && <>
            <Drawer open={previewOpen} onClose={() => setPreviewOpen(false)} placement="bottom" height="92%" title={t('dash.preview')} className="dash-preview-drawer">
              <PhonePreview user={user} />
            </Drawer>
          </>}
        </div>
      </AntApp>
    </ConfigProvider>
  );
};

export default Dashboard;
